/**
 * Transport-agnostic API primitives shared by the data layer.
 * (ARCHITECTURE.md §3 — finalizes the error model + pagination contract.)
 */

/** Normalized error thrown by the HTTP client for any non-2xx response. */
export class ApiError extends Error {
  readonly status: number;
  /** Parsed response body, if any (often `{ error | message }`). */
  readonly body: unknown;
  /** Correlates with the `x-request-id` we send, for log/trace lookup. */
  readonly requestId: string | undefined;

  constructor(
    status: number,
    message: string,
    options?: { body?: unknown; requestId?: string },
  ) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.body = options?.body;
    this.requestId = options?.requestId;
  }

  get isUnauthorized() {
    return this.status === 401;
  }
  get isForbidden() {
    return this.status === 403;
  }
  get isNotFound() {
    return this.status === 404;
  }
}

/**
 * Whether a thrown value is a 401. Surfaces that hand-roll their own query
 * error UI (instead of `QueryBoundary`) use this to branch to the login prompt,
 * so "session expired" reads the same everywhere.
 */
export function isUnauthorizedError(error: unknown): boolean {
  return error instanceof ApiError && error.isUnauthorized;
}

/**
 * Whether a thrown value is a 404 whose body parsed as JSON.
 *
 * Several backend endpoints report "nothing to show" as **404 with a JSON
 * message** rather than `200 []` — `payments/transactions` answers
 * `404 {"message":"No transactions found"}` for a filter that matches no rows.
 * `http.ts` normalizes every non-2xx into an `ApiError`, so without an explicit
 * exception the query rejects and the page shows "We couldn't load…" + Retry to
 * a user whose only problem is having no data yet.
 *
 * The JSON-body guard is what keeps this narrow: a *routing* 404 (bad backend
 * mount, proxy miss, unimplemented route) answers with an HTML page or bare
 * text, which the HTTP client parses to a string — that still throws rather
 * than silently reading as "you have none". Use `isRouteMissingError` when the
 * route genuinely does not exist.
 *
 * There is deliberately no central "treat every error as empty" rule: each
 * endpoint reports emptiness differently, and swallowing errors app-wide would
 * turn real outages into innocent-looking blank pages. Opt in per endpoint, in
 * the repository, with a comment recording what the backend actually returns.
 */
export function isNoRowsError(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    error.isNotFound &&
    typeof error.body === "object" &&
    error.body !== null
  );
}

/**
 * Whether a thrown value is any 404, JSON body or not.
 *
 * For endpoints the backend has **never implemented**, where a 404 is the
 * permanent answer rather than a data-dependent one (`job/my_offers` returns a
 * bare `Route does not exist`). Prefer `isNoRowsError`; reach for this only
 * where a missing route is the documented state of the feature, and say so at
 * the call site.
 */
export function isRouteMissingError(error: unknown): boolean {
  return error instanceof ApiError && error.isNotFound;
}

/**
 * `http.ts`'s last-resort message when the body named no reason. It's a status
 * code in a sentence — never show it to a user.
 */
const STATUS_ONLY_MESSAGE = /^Request failed with status \d+$/;

/**
 * User-facing text for a failed mutation, preferring the backend's own words.
 *
 * The backend usually says the one thing the user can act on — "You don't have
 * enough balance", "Some devices offline or not found", "Invalid device code!"
 * — while the surface's own copy can only manage "Please try again", which
 * invites a retry that cannot succeed. `http.ts` has already folded the body's
 * `error`/`message`/`errors[]` text into `ApiError.message`, so that is the
 * text to show; `fallback` covers the two cases where it says nothing: a
 * non-`ApiError` throw, and the bare status-code sentence.
 */
export function apiErrorMessage(error: unknown, fallback: string): string {
  if (!(error instanceof ApiError)) return fallback;
  const message = error.message.trim();
  if (!message || STATUS_ONLY_MESSAGE.test(message)) return fallback;
  return message;
}

/**
 * One backend-reported problem with a submitted payload.
 *
 * `field` is the request-body key the backend blamed, or `"general"` when it
 * blamed the request as a whole. `code` is the backend's machine-readable tag
 * (`MIN_POSITIONS`, `INSUFFICIENT_BALANCE`, `too_small`, …) — useful for
 * branching, but never shown to the user; `message` is already human-readable.
 */
export interface ApiFieldError {
  field: string;
  message: string;
  code?: string;
}

/**
 * Per-field errors carried by a rejected request, in the two shapes this
 * backend actually uses.
 *
 * The modern shape is `{errors: [{field, message, code}]}` — `POST job/create`
 * answers a short-priced job with
 * `422 {"errors":[{"field":"job_price","message":"Insufficient balance. Required: $1.00, Available: $0.00","code":"INSUFFICIENT_BALANCE"}]}`.
 * Older routes instead answer `{msg: ["<b>…</b> is required"]}`, an array of
 * HTML fragments with no field attribution.
 *
 * Both carry the only text that tells a user what to actually *do*, and neither
 * is reachable through `Error.message`, so surfaces that render a submission
 * failure should read this rather than inventing their own copy. Returns `[]`
 * for transport failures and for bodies in neither shape — callers fall back to
 * `error.message`.
 */
export function getApiFieldErrors(error: unknown): ApiFieldError[] {
  if (!(error instanceof ApiError)) return [];
  return fieldErrorsFromBody(error.body);
}

/**
 * `getApiFieldErrors` over a raw parsed body, for the HTTP client — which needs
 * these while it is still *constructing* the `ApiError`.
 */
export function fieldErrorsFromBody(body: unknown): ApiFieldError[] {
  if (!isRecord(body)) return [];

  const { errors, msg } = body;

  if (Array.isArray(errors)) {
    return errors.flatMap((entry) => {
      if (!isRecord(entry) || typeof entry.message !== "string") return [];
      return [
        {
          field:
            typeof entry.field === "string" && entry.field
              ? entry.field
              : "general",
          message: entry.message,
          ...(typeof entry.code === "string" ? { code: entry.code } : {}),
        },
      ];
    });
  }

  // Legacy `msg: string[]`: HTML fragments, unattributed. Strip the tags so the
  // markup never reaches the DOM as text (or, worse, as `dangerouslySetInnerHTML`).
  if (Array.isArray(msg)) {
    return msg.flatMap((entry) => {
      if (typeof entry !== "string") return [];
      const message = entry.replace(/<[^>]*>/g, "").trim();
      return message ? [{ field: "general", message }] : [];
    });
  }

  return [];
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

/** Standard paginated envelope used across resource hooks. */
export interface Page<T> {
  items: T[];
  page: number;
  pageSize: number;
  total: number;
}

export function emptyPage<T>(pageSize = 20): Page<T> {
  return { items: [], page: 1, pageSize, total: 0 };
}
